import { useMemo, useState } from "react";
import { Copy, Send, Mail, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { PageHeader } from "@/components/UI";

/**
 * Admin-only library of pre-drafted customer emails.
 * Each template supports {{placeholders}} that render as editable inputs.
 * Copy the final rendered text OR send directly via connected Gmail.
 */

const TEMPLATES = [
  {
    id: "pridebank-onboarding",
    icon: "🎫",
    name: "Pride Bank — Ticket portal invite",
    audience: "Customer · pre-onboarding",
    description:
      "Invite Pride Bank contacts to start using the ticket portal while formal onboarding is still in progress.",
    placeholders: [
      { key: "contact_name", label: "Contact name", default: "Team", multiline: false },
      { key: "customer_email", label: "Recipient email", default: "procurement@pridebank.co.ug", multiline: false },
    ],
    subject: "Fast-track your Jero-One service requests — meet our new ticket portal",
    cc: "info@jero-onetechnologies.com, support@jero-onetechnologies.com",
    body: `Dear {{contact_name}},

Warm greetings from Jero-One Technologies.

While we finalise the onboarding paperwork for our upcoming engagement, we'd like to invite the Pride Bank team to already start experiencing the responsiveness and transparency that will define our partnership. To that end, we've provisioned your organisation on our Service Desk Portal — a secure, always-on channel through which you can:

  • Log any IT, CCTV, network or cybersecurity request in under 60 seconds
  • Loop in as many of your colleagues as you'd like (each ticket supports multiple CCs)
  • Track live progress against clear SLA timers
  • Access every quotation, job card and delivery note we produce for you — branded and archived in one place
  • Receive email updates automatically on every action we take on your ticket

Create your Pride Bank account (2 minutes):
https://workspace.jero-onetechnologies.com/register

After registering, you'll immediately land in the portal and can raise your first ticket. If you'd like additional colleagues to have their own login (e.g. for the IT desk, Operations, Compliance), simply forward this email — every registrant is auto-assigned to the Pride Bank tenant.

Why a ticket system — not just email or phone?

  Traditional email                            Jero-One Service Desk
  ──────────────────────────────────────────────────────────────────
  Requests get buried across inboxes           Unique ref (e.g. JOT/TKT/2026/017) that never dies
  Only the sender knows the status             Any authorised Pride Bank colleague sees real-time status
  No SLA visibility                            Automatic Response-due + Resolution-due timers
  Documents get lost in threads                Quotations / job cards / delivery notes attach to the ticket
  Handovers between our staff cause delays     Assignee is tracked; escalations are automatic
  No auditable history                         Full audit log of every comment, status change, and file

The result: shorter downtime, faster approvals, cleaner audit trails — critical for a financial institution held to CBK/BOU-grade operational standards.

What to do right now (5 minutes)

  1. Click the sign-up link above and register with your Pride Bank email.
  2. Create one test ticket (title it "Portal test — Pride Bank kickoff") and CC any 1-2 colleagues.
  3. Watch how our team acknowledges within the SLA — no phone calls needed.
  4. Reply to this email if anything is unclear; we're happy to walk your team through a 15-minute live demo.

We look forward to a smooth, transparent and professional working relationship — starting today, well before the formal go-live.

Warm regards,

Jerome Gerald Lutaya
Director · Jero-One Technologies Ltd.
+256 781 830 835  ·  info@jero-onetechnologies.com
www.jero-onetechnologies.com  ·  workspace.jero-onetechnologies.com

Uganda · USA · Canada`,
  },
];

function fillTemplate(tpl, values) {
  let out = tpl;
  for (const [k, v] of Object.entries(values)) {
    out = out.replaceAll(`{{${k}}}`, v || "");
  }
  return out;
}

export default function EmailTemplates() {
  const { toast } = useToast();
  const [activeId, setActiveId] = useState(TEMPLATES[0].id);
  const active = TEMPLATES.find((t) => t.id === activeId);
  const [values, setValues] = useState(() =>
    Object.fromEntries(active.placeholders.map((p) => [p.key, p.default || ""]))
  );
  const [sending, setSending] = useState(false);

  const rendered = useMemo(() => ({
    subject: fillTemplate(active.subject, values),
    body: fillTemplate(active.body, values),
  }), [active, values]);

  const copy = async () => {
    const text = `Subject: ${rendered.subject}\nCc: ${active.cc}\n\n${rendered.body}`;
    await navigator.clipboard.writeText(text);
    toast("Copied to clipboard", "success");
  };

  const sendNow = async () => {
    if (!values.customer_email) {
      toast("Please add the recipient email first", "error");
      return;
    }
    setSending(true);
    try {
      await api.sendGmail({
        to: values.customer_email,
        cc: active.cc,
        subject: rendered.subject,
        body: rendered.body,
      });
      toast("Sent via your connected Gmail", "success");
    } catch (e) {
      toast(
        e?.response?.data?.detail ||
          "Send failed — check that your Gmail is connected in Profile → Google.",
        "error"
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-email-templates" className="space-y-5">
      <PageHeader
        icon={Sparkles}
        title="Email templates"
        subtitle="Pre-drafted messages for common customer scenarios · Copy or send in one click"
      />

      <div className="grid md:grid-cols-[240px_1fr] gap-5">
        {/* Template list */}
        <aside className="card p-3 space-y-1 h-fit sticky top-4">
          {TEMPLATES.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setActiveId(t.id);
                setValues(Object.fromEntries(t.placeholders.map((p) => [p.key, p.default || ""])));
              }}
              data-testid={`tpl-${t.id}`}
              className={`w-full text-left px-3 py-2 rounded-md transition ${
                activeId === t.id
                  ? "bg-emerald-50 text-emerald-800 font-semibold"
                  : "hover:bg-slate-50 text-slate-700"
              }`}
            >
              <div className="text-sm flex items-center gap-2">
                <span>{t.icon}</span>
                <span className="flex-1">{t.name}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">{t.audience}</div>
            </button>
          ))}
        </aside>

        {/* Editor + preview */}
        <div className="space-y-4">
          <div className="card card-padded">
            <p className="text-xs muted mb-3">{active.description}</p>

            <div className="grid md:grid-cols-2 gap-3">
              {active.placeholders.map((p) => (
                <div key={p.key}>
                  <label className="label">{p.label}</label>
                  <input
                    className="input"
                    value={values[p.key] || ""}
                    onChange={(e) => setValues((v) => ({ ...v, [p.key]: e.target.value }))}
                    data-testid={`fld-${p.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="card p-5">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">Subject</div>
            <div className="font-bold text-slate-900 mb-4" data-testid="preview-subject">
              {rendered.subject}
            </div>

            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">Cc</div>
            <div className="text-sm text-slate-700 mb-4 font-mono text-xs">{active.cc}</div>

            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">Body</div>
            <pre
              className="whitespace-pre-wrap text-sm font-sans text-slate-800 bg-slate-50 border border-slate-100 rounded-lg p-4"
              data-testid="preview-body"
            >{rendered.body}</pre>

            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={copy}
                className="btn btn-ghost"
                data-testid="copy-template"
              >
                <Copy size={14} /> Copy to clipboard
              </button>
              <button
                onClick={sendNow}
                disabled={sending}
                className="btn btn-primary"
                data-testid="send-template"
                title="Sends via your connected Gmail account"
              >
                <Send size={14} />
                {sending ? "Sending…" : "Send via my Gmail"}
              </button>
            </div>
            <div className="text-[11px] text-slate-400 mt-2 flex items-center gap-1 justify-end">
              <Mail size={10} /> Sending requires Gmail connected in Profile → Google.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
